import { useEffect, useRef, useState } from 'react';

import { OptionType } from 'components/select/MultiSelectCheckbox';

import { useClickOutSide } from 'hooks/useClickOutside';

export const useMultiSelectCheckbox = (
  options: OptionType[],
  selectedValues: string[],
  onChange: (selected: OptionType[]) => void,
  localSearch: boolean | undefined,
  onSearch: (searchTerm: string) => void,
) => {
  const [isOpen, setIsOpen] = useState(false);
  const [localSearchTerm, setLocalSearchTerm] = useState('');
  const [dropdownPosition, setDropdownPosition] = useState<'bottom' | 'top'>('bottom');

  const triggerRef = useRef<HTMLDivElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useClickOutSide(dropdownRef, () => setIsOpen(false));

  useEffect(() => {
    if (!isOpen || !triggerRef.current) return;

    const updatePosition = () => {
      const triggerRect = triggerRef.current?.getBoundingClientRect();

      if (!triggerRect) return;

      const viewportHeight = window.innerHeight;
      const spaceBelow = viewportHeight - triggerRect.bottom;
      const requiredSpace = 300;

      const shouldPositionTop = spaceBelow < requiredSpace && triggerRect.top > requiredSpace;

      setDropdownPosition(shouldPositionTop ? 'top' : 'bottom');

      const dropdownElement = dropdownRef.current?.querySelector(
        '[data-dropdown="true"]',
      ) as HTMLElement;

      if (!dropdownElement) return;

      if (shouldPositionTop) {
        dropdownElement.style.bottom = `${viewportHeight - triggerRect.top}px`;
        dropdownElement.style.top = 'auto';

        return;
      }

      dropdownElement.style.top = `${triggerRect.bottom}px`;
      dropdownElement.style.bottom = 'auto';
    };

    updatePosition();
    window.addEventListener('scroll', updatePosition);
    window.addEventListener('resize', updatePosition);

    return () => {
      window.removeEventListener('scroll', updatePosition);
      window.removeEventListener('resize', updatePosition);
    };
  }, [isOpen]);

  const selectedOptions = options.filter((option) => selectedValues.includes(option.value));

  const filteredOptions =
    !localSearch || !localSearchTerm
      ? options
      : options.filter((option) =>
          [option.label, option.subLabel].some((text) =>
            String(text).toLowerCase().includes(localSearchTerm.toLowerCase()),
          ),
        );

  const toggleDropdown = () => setIsOpen((prev) => !prev);

  const handleOptionClick = (option: OptionType) => () => {
    const newSelectedOptions = selectedOptions.some((item) => item.value === option.value)
      ? selectedOptions.filter((item) => item.value !== option.value)
      : selectedOptions.concat(option);

    onChange(newSelectedOptions);
  };

  const handleRemove = (value: string) => () => {
    onChange(selectedOptions.filter((item) => item.value !== value));
  };

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;

    setLocalSearchTerm(value);
    onSearch(value);
  };

  return {
    isOpen,
    selectedOptions,
    handleOptionClick,
    handleRemove,
    toggleDropdown,
    localSearchTerm,
    filteredOptions,
    handleInputChange,
    dropdownRef,
    triggerRef,
    dropdownPosition,
  };
};
